import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import ChevronRightIcon from '../../../../assets/icons/chevron-right.svg?react';
import {
  usePrepareTransactionsMutation,
  useBroadcastAndTrackMutation,
  useCreateGiftMutation,
} from '../../../../redux/api/authorized/gifts.js';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive.js';
import GiftCard from '../../../../components/GiftCard/index.jsx';
import { toTokenAmount, useSolana } from '../../../../hooks/useSolana.js';
import { fromTokenAmount } from '../../../../hooks/useSolana';
import ConfirmationSkeleton from './skeleton.jsx';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
  selectTokensWithUSDBalance,
} from '../../../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../../../redux/slices/walletSlice';
import {
  EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_GIFT_CREATED,
  EVENT_GIFT_FLOW_GIFT_FAILED,
  EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTED,
  EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTOR_OPENED,
  sendEvent,
} from '../../../../common/analytics.js';
import {
  formatUSD,
  formatUSDEnsure2Digits,
} from '../../../../common/currency.js';
import ScrollRestore from '../../../../components/ScrollRestore/index.tsx';
import { useGiftForm } from '../../../../components/GiftFormContext/index.jsx';
import Button from '../../../../components/Button/index.tsx';
import SendingStatus from '../../../../views/Gifts/SendingStatus/index.jsx';
import { hapticsImpactMedium } from '../../../../common/haptic.js';
import { useLazyCreateOrderQuery } from '../../../../redux/api/authorized/ultra.ts';
import { openModal } from '../../../../redux/slices/modals.js';
import { useGetGiftFeesQuery } from '../../../../redux/api/authorized/giftFees.js';
import useApplePay, {
  BALANCE_TOLERANCE,
  calculateApplePayFeeAndTotal,
} from '../../../../hooks/useApplePay.js';
import useApplePayCheckout from '../../../../hooks/useApplePayCheckout.js';
import { ApplePayWidget } from '../../../../components/ApplePayWidget';
import ApplePayButton from '../../../../components/ApplePayButton';
import ApplePayMark from '../../../../assets/icons/applepay.svg?react';
import PhoneVerification from './components/PhoneVerification';
import { useGetCurrentUserProfileQuery } from '../../../../redux/api/authorized/auth';
import { formatDate } from '../../../../common/date.js';
import { useCreateCustomGiftCardDesignMutation } from '../../../../redux/api/authorized/customGiftCardDesigns';
import { compressBase64Image } from '../../../../common/images';

const GiftCardContainer = styled.div`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  flex: 1;

  .stripe-onramp-wrapper {
    margin: 0;
    padding: 0;
  }
`;

const Line = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const Message = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 4px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const ContentRow = styled.div`
  display: flex;
  width: 100%;
  gap: 48px;
  color: ${({ theme }) => theme.text.regular};
  flex-direction: column;
`;

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  flex-direction: row;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 20px;
  }
`;

const GiftCardWrapper = styled.div`
  width: 100%;
  max-width: 50%;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
    width: 100%;
  }
`;

const SummaryWrapper = styled.div`
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const PaymentSelectorContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  cursor: pointer;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
`;

const TokenImage = styled.img`
  width: 24px;
  height: 24px;
`;

const PaymentTokenInfoText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
`;

const Confirmation = ({ isModal, currentStep }) => {
  const theme = useTheme();
  const { applePayEnabled } = useApplePay();
  const { data: currentUser } = useGetCurrentUserProfileQuery();
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);

  const [triggerCreateOrder] = useLazyCreateOrderQuery();
  const [createGift] = useCreateGiftMutation();
  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();

  const [prepareTransactions] = usePrepareTransactionsMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();

  const [formatedTokenAmount, setFormatedTokenAmount] = useState(0);
  const [formatedAmountUSD, setFormatedAmountUSD] = useState(0);
  const [pendingApplePayUsdAmount, setPendingApplePayUsdAmount] =
    useState(null);
  const [waitingForApplePayBalance, setWaitingForApplePayBalance] =
    useState(false);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );
  const tokensWithUSDBalance = useSelector(selectTokensWithUSDBalance);

  const dispatch = useDispatch();

  const usdcTokenBalance = useSelector((state) =>
    selectTokenBalance('solana', usdcToken?.address)(state),
  );

  const {
    feedback,
    network,
    image,
    targetTokenAddress,
    targetTokenAmount,
    delivery,
    setSelectedPaymentToken,
    email,
    message,
    setFeedback,
    sendingStatus,
    setSendingStatus,
    claimUrl,
    setClaimUrl,
    fromUsdcDirection,
    usdAmount,
    selectedPaymentToken,
    selectedPaymentMethodType,
    setSelectedPaymentMethodType,
    senderName,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    country,
    recipientName,
    setValue,
    trigger,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const [fullMessage, setFullMessage] = useState(false);

  const [tokensWithEnoughBalance, setTokensWithEnoughBalance] = useState(null); // null = not calculated yet
  const [tokensWithoutEnoughBalance, setTokensWitoutEnoughBalance] = useState(
    [],
  );
  const [
    userSelectedPaymentTokenUsdcBalance,
    setUserSelectedPaymentTokenUsdcBalance,
  ] = useState(0);

  const userSelectedPaymentToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(
      state,
      network,
      selectedPaymentToken?.address,
    ),
  );

  const { data: giftFeesResponse, isLoading: isLoadingGiftFees } =
    useGetGiftFeesQuery(
      {
        token: targetToken?.address,
        tokenAmount: targetTokenAmount,
        delivery: delivery,
      },
      {
        skip: !targetToken?.address || !targetTokenAmount || !delivery,
        refetchOnMountOrArgChange: true,
        keepUnusedDataFor: 0,
      },
    );

  // Save local data URL image to backend when user reaches confirmation (they're logged in)
  useEffect(() => {
    const saveLocalImageIfNeeded = async () => {
      if (!image || !image.startsWith('data:')) {
        // Not a data URL, nothing to save
        return;
      }

      try {
        const compressedImage = await compressBase64Image(image);
        const response = await createCustomGiftCardDesign({
          image: compressedImage,
          type: delivery === 'mail' ? 'physical' : 'digital',
        });

        if (response.data?.imageUrl) {
          setValue('image', response.data.imageUrl, { shouldValidate: true });
          await trigger('image');
        }
      } catch (error) {
        console.error('Failed to save local image:', error);
        // Don't block the flow if image save fails
      }
    };

    saveLocalImageIfNeeded();
  }, [image, delivery, createCustomGiftCardDesign, setValue, trigger]); // Run when image or delivery changes

  useEffect(() => {
    if (userSelectedPaymentToken && tokensWithEnoughBalance) {
      const usToken = tokensWithEnoughBalance.find(
        (token) => token.address === userSelectedPaymentToken?.address,
      );
      if (usToken) {
        setUserSelectedPaymentTokenUsdcBalance(usToken.usdValue);
      } else {
        setUserSelectedPaymentTokenUsdcBalance(0);
      }
    }
  }, [userSelectedPaymentToken, tokensWithEnoughBalance]);

  const {
    parseSolanaError,
    signAndSerializeTransaction,
    refreshTokenBalances,
  } = useSolana();

  const {
    startCheckout: startApplePayCheckout,
    widgetProps: applePayWidgetProps,
    isStarting: applePayStarting,
    isProcessingPayment: applePayProcessing,
    reset: resetApplePayCheckout,
  } = useApplePayCheckout();

  const [loadingTokenAmount, setLoadingTokenAmount] = useState(false);

  // Simple polling: refresh balances every 3 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenBalances({ force: true });
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshTokenBalances]);

  // Validation: Check if currently selected token still has enough balance
  // Runs when tokensWithEnoughBalance or selectedPaymentToken changes
  // Clears selection if the selected token no longer has sufficient balance
  useEffect(() => {
    // Skip if data not ready or no payment method selected
    if (
      tokensWithEnoughBalance === null ||
      !selectedPaymentMethodType ||
      selectedPaymentMethodType !== 'token'
    ) {
      return;
    }

    // Skip if Apple Pay is in progress (don't interfere)
    if (
      selectedPaymentMethodType === 'applePay' &&
      (waitingForApplePayBalance || applePayProcessing || applePayStarting)
    ) {
      return;
    }

    // Check if selected token still has enough balance
    if (selectedPaymentToken && tokensWithEnoughBalance.length > 0) {
      const selectedTokenHasBalance = tokensWithEnoughBalance.some(
        (token) => token.address === selectedPaymentToken.address,
      );

      // If token no longer has enough balance, clear the selection
      if (!selectedTokenHasBalance) {
        setSelectedPaymentMethodType(null);
        setSelectedPaymentToken(usdcToken); // Reset to USDC for calculations
      }
    } else if (tokensWithEnoughBalance.length === 0 && selectedPaymentToken) {
      // No tokens have enough balance - clear token selection
      setSelectedPaymentMethodType(null);
      setSelectedPaymentToken(usdcToken); // Reset to USDC for calculations
    }
  }, [
    tokensWithEnoughBalance,
    selectedPaymentToken,
    selectedPaymentMethodType,
    waitingForApplePayBalance,
    applePayProcessing,
    applePayStarting,
    usdcToken,
    setSelectedPaymentToken,
    setSelectedPaymentMethodType,
  ]);

  // Auto-selection: Automatically select payment method when data is ready
  // Runs when tokensWithEnoughBalance changes and no payment method is selected
  // Only auto-selects if user hasn't made a selection yet
  useEffect(() => {
    // Skip if required data not ready
    if (!targetToken || !usdcToken || !usdAmount) {
      return;
    }

    // Wait for balance calculations to complete
    if (tokensWithEnoughBalance === null) {
      return;
    }

    // Don't override user's Apple Pay selection if in progress
    if (
      selectedPaymentMethodType === 'applePay' &&
      (waitingForApplePayBalance || applePayProcessing || applePayStarting)
    ) {
      return;
    }

    // Only auto-select if no payment method is currently selected
    if (selectedPaymentMethodType) {
      return;
    }

    if (tokensWithEnoughBalance.length > 0) {
      // Prefer target token if it has enough balance (no swap needed)
      const targetTokenWithBalance = tokensWithEnoughBalance.find(
        (token) => token.address === targetToken.address,
      );

      if (targetTokenWithBalance) {
        // Target token has enough balance - use it (no swap needed)
        setSelectedPaymentToken(targetTokenWithBalance);
        setSelectedPaymentMethodType('token');
      } else {
        // Target token doesn't have enough balance - select token with highest balance
        const tokenWithMostBalance = tokensWithEnoughBalance.reduce(
          (max, token) => {
            return (token.usdValue || 0) > (max.usdValue || 0) ? token : max;
          },
          tokensWithEnoughBalance[0],
        );

        setSelectedPaymentToken(tokenWithMostBalance);
        setSelectedPaymentMethodType('token');
      }
    } else {
      // No tokens have enough balance - default to USDC for calculations
      // Don't set payment method type (user must select via PaymentSuggestion)
      setSelectedPaymentToken(usdcToken);

      // Optionally default to Apple Pay if enabled and total amount (gift + shipping + Apple Pay fee) meets minimum
      const giftAmount = parseFloat(formatedAmountUSD || 0);
      const shippingFees = parseFloat(
        giftFeesResponse?.totalFees?.usdFees || 0,
      );
      const baseAmount = giftAmount + shippingFees;
      // Calculate Apple Pay fee for the check using shared calculation
      const { total: totalAmount } = calculateApplePayFeeAndTotal(baseAmount);
      if (applePayEnabled && totalAmount >= 5) {
        setSelectedPaymentMethodType('applePay');
      }
    }
  }, [
    tokensWithEnoughBalance,
    targetToken,
    usdcToken,
    usdAmount,
    applePayEnabled,
    selectedPaymentMethodType,
    waitingForApplePayBalance,
    applePayProcessing,
    applePayStarting,
    setSelectedPaymentToken,
    setSelectedPaymentMethodType,
    formatedAmountUSD,
    giftFeesResponse,
  ]);

  // Centralized fee calculations
  const feeCalculations = useMemo(() => {
    const giftAmount = parseFloat(formatedAmountUSD || 0);
    const shippingFees = parseFloat(giftFeesResponse?.totalFees?.usdFees || 0);
    const usdDiscount = parseFloat(
      giftFeesResponse?.totalFees?.usdDiscount || 0,
    );
    const baseAmount = giftAmount + shippingFees; // Amount before Apple Pay fee

    // Calculate Apple Pay fee and total using the correct inverse formula
    // Fee is taken from the total amount charged, so we use the shared calculation
    const { fee: applePayFee, total: totalAmount } =
      selectedPaymentMethodType === 'applePay'
        ? calculateApplePayFeeAndTotal(baseAmount)
        : { fee: 0, total: baseAmount };

    return {
      giftAmount,
      shippingFees,
      usdDiscount,
      applePayFee,
      totalAmount,
      applePayBaseAmount: baseAmount, // Amount sent to Apple Pay (they add fee on top)
    };
  }, [
    formatedAmountUSD,
    giftFeesResponse, // Use whole object instead of nested property to ensure updates are detected
    selectedPaymentMethodType,
    calculateApplePayFeeAndTotal, // Include function reference for dependency tracking
  ]);

  useEffect(() => {
    const totalAmountWithFees =
      feeCalculations.giftAmount + feeCalculations.shippingFees;

    if (tokensWithUSDBalance && totalAmountWithFees) {
      setTokensWithEnoughBalance(
        tokensWithUSDBalance.filter(
          (token) => token.usdValue >= totalAmountWithFees,
        ),
      );

      setTokensWitoutEnoughBalance(
        tokensWithUSDBalance.filter(
          (token) => token.usdValue < totalAmountWithFees,
        ),
      );
    } else {
      // Reset to null if we don't have the data yet
      setTokensWithEnoughBalance(null);
    }
  }, [tokensWithUSDBalance, feeCalculations]);

  const userSelectedTokenBalance = useSelector((state) =>
    selectTokenBalance(network, selectedPaymentToken?.address)(state),
  );

  // TODO: Get estimated delivery date from backend
  const estimatedDeliveryDate = '7-10 business days';
  const createGiftHandler = async () => {
    sendEvent(EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED);
    try {
      setSendingStatus('sending');

      // Determine the payment token to use
      // selectedPaymentToken is already set to USDC when Apple Pay is selected
      const paymentToken =
        selectedPaymentMethodType === 'applePay'
          ? usdcToken
          : selectedPaymentToken;

      // Step 1: Calculate baseTokenAmount based on USD value or user-entered base token amount
      let baseTokenAmount;

      // Check if user entered amount in base token (payment token)
      // This happens when paymentToken === targetToken AND fromUsdcDirection === false
      if (paymentToken.address === targetToken.address && !fromUsdcDirection) {
        // User entered amount in base token directly - use it as-is
        baseTokenAmount = toTokenAmount(
          targetTokenAmount,
          paymentToken.decimals,
        );
      } else if (paymentToken.address === usdcToken.address) {
        // If paying with USDC, use usdAmount directly
        baseTokenAmount = toTokenAmount(usdAmount, paymentToken.decimals);
      } else {
        // For other tokens, get a quote for the USD value
        const orderResult = await triggerCreateOrder({
          inputMint: usdcToken.address,
          outputMint: paymentToken.address,
          amount: toTokenAmount(usdAmount, usdcToken.decimals),
        }).unwrap();

        baseTokenAmount = orderResult.outAmount;
      }

      // Validate baseTokenAmount is set
      if (!baseTokenAmount || baseTokenAmount === 0) {
        sendEvent(EVENT_GIFT_FLOW_GIFT_FAILED, {
          error: 'Unable to calculate payment amount. Please try again.',
        });
        throw new Error(
          'Unable to calculate payment amount. Please try again.',
        );
      }

      // Step 2: Prepare gift data
      const giftData = {
        delivery: delivery,
        email: email,
        token: targetToken.address, // Output token (what receiver gets)
        baseToken: paymentToken.address, // Input token (what creator pays with)
        baseTokenAmount: baseTokenAmount, // Amount in baseToken (required by backend)
        network: network,
        imageUrl: image,
        message: message,
      };

      if (delivery === 'mail') {
        giftData.addressLine1 = addressLine1;
        giftData.addressLine2 = addressLine2;
        giftData.city = city;
        giftData.state = state;
        giftData.postalCode = postalCode;
        giftData.senderName = senderName;
        giftData.country = country;
        giftData.recipientName = recipientName;
      }

      // Step 3: Create gift
      setFeedback('Creating gift...');
      const giftResponse = await createGift(giftData).unwrap();
      const gift = giftResponse?.gift;

      // Step 4: Prepare transactions (unified endpoint)
      setFeedback('Preparing transactions...');
      const prepareResult = await prepareTransactions(gift.id).unwrap();

      // Step 5: Sign and broadcast (unified endpoint)
      setFeedback('Please sign transaction...');

      if (prepareResult.swapTransaction) {
        // Gift with swap - sign both transactions
        const signedSwapTx = await signAndSerializeTransaction(
          prepareResult.swapTransaction,
        );

        const signedSecondTx = await signAndSerializeTransaction(
          prepareResult.htlcTransaction || prepareResult.transferTransaction,
        );

        setFeedback('Broadcasting transactions...');

        // Unified endpoint handles all gift types with swap
        const result = await broadcastAndTrack({
          giftId: gift.id,
          serializedTransaction: signedSwapTx, // Swap transaction
          serializedSecondTransaction: signedSecondTx, // HTLC or transfer transaction
          requestId: prepareResult.requestId, // Jupiter Ultra request ID
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      } else {
        // Gift without swap - single transaction
        const signedTx = await signAndSerializeTransaction(
          prepareResult.serializedTransaction,
        );

        setFeedback('Broadcasting transaction...');

        // Unified endpoint handles all gift types without swap
        const result = await broadcastAndTrack({
          giftId: gift.id,
          serializedTransaction: signedTx,
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      }

      sendEvent(EVENT_GIFT_FLOW_GIFT_CREATED, {
        id: gift.id,
        token: gift.token,
        base_token: gift.baseToken,
        base_token_amount: gift.baseTokenAmount,
        created_date: new Date().toISOString(),
      });

      refreshTokenBalances();
      hapticsImpactMedium();
    } catch (error) {
      setSendingStatus('error');
      const errorMessage = parseSolanaError(error);
      sendEvent(EVENT_GIFT_FLOW_GIFT_FAILED, {
        error: errorMessage,
      });
      setFeedback(`Error: ${errorMessage}`);
    }
  };

  useEffect(() => {
    return () => {
      resetApplePayCheckout();
      setPendingApplePayUsdAmount(null);
      setWaitingForApplePayBalance(false);
    };
  }, [resetApplePayCheckout]);

  // Reset Apple Pay state when payment method changes away from Apple Pay
  useEffect(() => {
    if (selectedPaymentMethodType !== 'applePay') {
      if (
        applePayWidgetProps ||
        pendingApplePayUsdAmount ||
        waitingForApplePayBalance
      ) {
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      }
    }
  }, [
    selectedPaymentMethodType,
    applePayWidgetProps,
    pendingApplePayUsdAmount,
    waitingForApplePayBalance,
    resetApplePayCheckout,
  ]);

  const startApplePayFlow = useCallback(() => {
    const resolvedUsdAmount = fromUsdcDirection
      ? parseFloat(usdAmount ?? '0')
      : parseFloat(formatedAmountUSD ?? '0');

    if (!resolvedUsdAmount || Number.isNaN(resolvedUsdAmount)) {
      setFeedback('Unable to determine amount for Apple Pay checkout.');
      return;
    }

    // Recalculate amount fresh to ensure we have the latest fees
    // This prevents using stale feeCalculations if it hasn't updated yet
    const giftAmount = parseFloat(formatedAmountUSD || 0);
    const shippingFees = parseFloat(giftFeesResponse?.totalFees?.usdFees || 0);
    const applePayBaseAmount = giftAmount + shippingFees;

    // Validate that shipping fees are available for physical delivery
    if (delivery === 'mail' && shippingFees <= 0) {
      setFeedback('Unable to calculate shipping fees. Please try again.');
      return;
    }

    startApplePayCheckout(applePayBaseAmount, {
      onPending: () => {
        setFeedback(null);
      },
      onSuccess: () => {
        setFeedback('Waiting for Apple Pay funds to settle...');
        setSendingStatus('sending');
        // Store the total amount (gift + shipping) that we need to wait for
        setPendingApplePayUsdAmount(applePayBaseAmount);
        setWaitingForApplePayBalance(true);
      },
      onError: (err) => {
        if (err) {
          setFeedback('Unable to start Apple Pay. Please try again.');
        } else {
          setFeedback('Apple Pay payment failed. Please try again.');
        }
        setSendingStatus('idle');
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onAlert: (title, message, type) => {
        if (type === 'error') {
          setFeedback(
            `Apple Pay error: ${title || message || 'Payment failed'}`,
          );
          resetApplePayCheckout();
          setPendingApplePayUsdAmount(null);
          setWaitingForApplePayBalance(false);
          setSendingStatus('idle');
        } else if (type === 'info') {
          setFeedback(message || title || null);
        }
      },
    });
  }, [
    formatedAmountUSD,
    fromUsdcDirection,
    resetApplePayCheckout,
    setFeedback,
    setSendingStatus,
    setPendingApplePayUsdAmount,
    setWaitingForApplePayBalance,
    startApplePayCheckout,
    usdAmount,
    feeCalculations,
    giftFeesResponse,
    delivery,
  ]);

  const handleApplePayPress = useCallback(() => {
    if (!applePayEnabled || applePayStarting || applePayProcessing) {
      return;
    }

    // Check if total amount (gift + shipping fees + Apple Pay fee) meets $5 minimum
    const totalAmount = feeCalculations.totalAmount;

    if (totalAmount < 5) {
      setFeedback('Apple Pay requires a minimum of $5');
      return;
    }

    // Check if phone is verified - if not, show inline phone verification
    if (!currentUser?.phoneNumber) {
      setShowPhoneVerification(true);
      return;
    }

    // Phone is verified, proceed with Apple Pay
    startApplePayFlow();
  }, [
    applePayEnabled,
    applePayProcessing,
    applePayStarting,
    currentUser?.phoneNumber,
    startApplePayFlow,
    feeCalculations,
    setFeedback,
  ]);

  const handlePhoneVerified = useCallback(() => {
    setShowPhoneVerification(false);
    // After phone verification, proceed with Apple Pay
    startApplePayFlow();
  }, [startApplePayFlow]);

  const handlePhoneVerificationBack = useCallback(() => {
    setShowPhoneVerification(false);
  }, []);

  // Check if Apple Pay balance becomes sufficient (polling happens separately every 3 seconds)
  useEffect(() => {
    if (!waitingForApplePayBalance || pendingApplePayUsdAmount == null) {
      return;
    }

    const numericBalance = parseFloat(usdcTokenBalance ?? '0');
    if (!Number.isNaN(numericBalance)) {
      // Use tolerance to account for minor USDC fluctuations and floating point precision
      const requiredBalance = Math.max(
        0,
        pendingApplePayUsdAmount - BALANCE_TOLERANCE,
      );
      if (numericBalance >= requiredBalance) {
        setWaitingForApplePayBalance(false);
        setPendingApplePayUsdAmount(null);
        // Show clear feedback that payment is processing, not just received
        setFeedback('Payment processing...');
        if (
          selectedPaymentMethodType === 'applePay' &&
          usdcToken &&
          (!selectedPaymentToken ||
            selectedPaymentToken.address !== usdcToken.address)
        ) {
          setSelectedPaymentToken(usdcToken);
        }
        setTimeout(() => {
          createGiftHandler();
        }, 0);
        resetApplePayCheckout();
      }
    }
  }, [
    waitingForApplePayBalance,
    pendingApplePayUsdAmount,
    usdcTokenBalance,
    usdcToken,
    selectedPaymentMethodType,
    selectedPaymentToken,
    createGiftHandler,
    resetApplePayCheckout,
    setFeedback,
    setSelectedPaymentToken,
  ]);

  // Calculate proper amounts and token values
  useEffect(() => {
    (async () => {
      // Basic null checks
      if (!usdcToken?.address || !targetToken?.address) {
        return;
      }

      // selectedPaymentToken is always USDC, so we can always calculate amounts
      // If target token is USDC, set values directly
      if (usdcToken.address === targetToken.address) {
        if (usdAmount) {
          setFormatedTokenAmount(targetTokenAmount);
          setFormatedAmountUSD(usdAmount);
        }
        return;
      }

      // For non-USDC target tokens, calculate based on direction
      if (fromUsdcDirection) {
        // User entered USD amount - set it immediately (no need to wait for API call)
        if (usdAmount) {
          setFormatedAmountUSD(usdAmount);

          // Convert to target token (async, but don't block USD display)
          if (triggerCreateOrder) {
            const orderResult = await triggerCreateOrder({
              inputMint: usdcToken.address,
              outputMint: targetToken.address,
              amount: toTokenAmount(usdAmount, usdcToken.decimals),
            }).unwrap();

            setFormatedTokenAmount(
              fromTokenAmount(orderResult.outAmount, targetToken.decimals),
            );
          }
        }
      } else {
        // User entered target token amount, convert to USD
        if (targetTokenAmount && triggerCreateOrder) {
          const orderResult = await triggerCreateOrder({
            inputMint: targetToken.address,
            outputMint: usdcToken.address,
            amount: toTokenAmount(targetTokenAmount, targetToken.decimals),
          }).unwrap();

          setFormatedTokenAmount(targetTokenAmount);
          setFormatedAmountUSD(
            fromTokenAmount(orderResult.outAmount, usdcToken.decimals),
          );
        }
      }
    })();
  }, [
    fromUsdcDirection,
    targetTokenAmount,
    usdAmount,
    targetToken,
    usdcToken,
    triggerCreateOrder,
  ]);

  if (!targetToken) {
    return <ConfirmationSkeleton />;
  }

  if (sendingStatus === 'sending' || sendingStatus === 'success') {
    return (
      <>
        <SendingStatus
          giftUrl={claimUrl}
          email={email}
          token={targetToken}
          cardImage={image}
          isSuccess={sendingStatus === 'success'}
          usdAmount={fromUsdcDirection ? formatedAmountUSD : undefined}
          tokenAmount={
            fromUsdcDirection ? formatedTokenAmount : targetTokenAmount
          }
          estimatedDeliveryDate={estimatedDeliveryDate}
          isPhysical={delivery === 'mail'}
        />
      </>
    );
  }

  // Show phone verification inline if needed
  if (showPhoneVerification) {
    return (
      <>
        <ScrollRestore />
        <DemoContainer>
          <PhoneVerification
            onVerified={handlePhoneVerified}
            onBack={handlePhoneVerificationBack}
          />
        </DemoContainer>
      </>
    );
  }

  return (
    <>
      <ScrollRestore />
      {/* Only render Apple Pay widget when Apple Pay is selected and processing */}
      {selectedPaymentMethodType === 'applePay' && applePayWidgetProps && (
        <ApplePayWidget {...applePayWidgetProps} />
      )}
      <DemoContainer>
        <ContentRow>
          <TwoColumnContainer>
            <GiftCardWrapper>
              <GiftCardContainer>
                <GiftCard
                  usdAmount={fromUsdcDirection ? usdAmount : undefined}
                  tokenAmount={
                    !fromUsdcDirection ? targetTokenAmount : formatedTokenAmount
                  }
                  cardImage={image}
                  token={targetToken}
                  isPhysical={delivery === 'mail'}
                  giftCardText={message}
                  senderName={senderName}
                  displayImageSelector={false}
                />
              </GiftCardContainer>
            </GiftCardWrapper>
            <SummaryWrapper>
              <FormContainer>
                <TitleRow>
                  <SummaryTitle>{targetToken.symbol} Gift Card</SummaryTitle>
                  <SummaryTitle>
                    {formatUSD(feeCalculations.totalAmount)}
                  </SummaryTitle>
                </TitleRow>
                <SummaryRow>
                  <SummaryDescription>
                    <span style={{ color: `${theme.text.secondary}` }}>
                      Gift Amount:
                    </span>{' '}
                    {formatUSD(formatedAmountUSD)}
                  </SummaryDescription>
                </SummaryRow>
                {(feeCalculations.shippingFees > 0 ||
                  feeCalculations.usdDiscount > 0) && (
                  <>
                    <SummaryRow>
                      <SummaryDescription
                        style={{
                          textDecoration:
                            feeCalculations.usdDiscount > 0
                              ? 'line-through'
                              : 'none',
                        }}
                      >
                        <span style={{ color: `${theme.text.secondary}` }}>
                          Shipping Fees:
                        </span>{' '}
                        {formatUSD(
                          feeCalculations.usdDiscount > 0
                            ? feeCalculations.usdDiscount
                            : feeCalculations.shippingFees,
                        )}
                      </SummaryDescription>
                    </SummaryRow>
                    {feeCalculations.usdDiscount > 0 && (
                      <SummaryRow>
                        <SummaryDescription>
                          <span style={{ color: `${theme.text.success}` }}>
                            First Gift Discount!
                          </span>{' '}
                        </SummaryDescription>
                      </SummaryRow>
                    )}
                  </>
                )}
                {selectedPaymentMethodType === 'applePay' &&
                  feeCalculations.applePayFee > 0 && (
                    <SummaryRow>
                      <SummaryDescription>
                        <span style={{ color: `${theme.text.secondary}` }}>
                          Payment Fee:
                        </span>{' '}
                        {formatUSD(feeCalculations.applePayFee)}
                      </SummaryDescription>
                    </SummaryRow>
                  )}
                {delivery === 'mail' && senderName && (
                  <SummaryRow>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        From:
                      </span>{' '}
                      {senderName}
                    </SummaryDescription>
                  </SummaryRow>
                )}
                {delivery === 'email' && (
                  <SummaryRow>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        Receiver:
                      </span>{' '}
                      {email}
                    </SummaryDescription>
                  </SummaryRow>
                )}
                {message && (
                  <SummaryRow style={{ flexDirection: 'column' }}>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        Message
                      </span>
                    </SummaryDescription>
                    <SummaryDescription>
                      {fullMessage ? message : `${message.slice(0, 20)}...`}{' '}
                      {!fullMessage && (
                        <span
                          onClick={() => setFullMessage(true)}
                          style={{
                            color: `${theme.text.secondary}`,
                            cursor: 'pointer',
                          }}
                        >
                          See more
                        </span>
                      )}
                    </SummaryDescription>
                  </SummaryRow>
                )}
              </FormContainer>
            </SummaryWrapper>
          </TwoColumnContainer>
        </ContentRow>
        {
          <ContentRow>
            <FormContainer>
              {delivery === 'mail' && addressLine1 && (
                <SummaryRow style={{ marginTop: 20 }}>
                  <SummaryDescription>
                    <span style={{ color: `${theme.text.secondary}` }}>
                      Shipping Address:
                    </span>{' '}
                    {[
                      addressLine1,
                      addressLine2,
                      [city, state, postalCode].filter(Boolean).join(', '),
                      country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </SummaryDescription>
                </SummaryRow>
              )}
              {delivery === 'mail' && (
                <SummaryDescription
                  style={{
                    fontStyle: 'italic',
                    textAlign: 'left',
                    width: '100%',
                    marginTop: 8,
                  }}
                >
                  Estimated delivery: {estimatedDeliveryDate}
                </SummaryDescription>
              )}
            </FormContainer>
          </ContentRow>
        }
        <Line />
        <ContentRow>
          <FormContainer>
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 20,
                width: '100%',
                flexDirection: 'column',
              }}
            >
              <SummaryDescription>Paying with</SummaryDescription>
              <PaymentSelectorContainer
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  // Always allow opening payment selection if we have token data
                  if (
                    tokensWithUSDBalance ||
                    tokensWithEnoughBalance !== null
                  ) {
                    sendEvent(EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTOR_OPENED);
                    dispatch(
                      openModal({
                        name: 'paymentSuggestion',
                        props: {
                          amount:
                            feeCalculations.giftAmount +
                            feeCalculations.shippingFees,
                          targetToken: targetToken,
                          onCancel: () => {
                            dispatch(
                              openModal({
                                name: 'giftFlow',
                                props: {
                                  initialStep: delivery === 'mail' ? 3 : 2,
                                },
                              }),
                            );
                          },
                          onPaymentMethodSelected: (paymentData) => {
                            sendEvent(EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTED, {
                              paymentMethod: paymentData,
                            });
                            if (paymentData.type === 'token') {
                              setSelectedPaymentMethodType('token');
                              // If user selected a specific token, use it; otherwise USDC is already set as default
                              if (paymentData.token) {
                                setSelectedPaymentToken(paymentData.token);
                              }
                            } else if (paymentData.type === 'applePay') {
                              setSelectedPaymentMethodType('applePay');
                              // Apple Pay always uses USDC (already set as default)
                            }
                            dispatch(
                              openModal({
                                name: 'giftFlow',
                                props: {
                                  initialStep: delivery === 'mail' ? 3 : 2,
                                },
                              }),
                            );
                          },
                        },
                      }),
                    );
                  }
                }}
              >
                {selectedPaymentMethodType === 'applePay' ? (
                  <>
                    <ApplePayMark
                      style={{
                        width: 40,
                        height: 'auto',
                      }}
                    />
                    <PaymentTokenInfoText>Apple Pay</PaymentTokenInfoText>
                    <ChevronRightIcon width={12} height={12} />
                  </>
                ) : selectedPaymentMethodType === 'token' &&
                  selectedPaymentToken ? (
                  <>
                    <TokenImage src={selectedPaymentToken?.logoURI} />
                    <PaymentTokenInfoText>
                      {selectedPaymentToken?.symbol} (
                      {fromUsdcDirection
                        ? formatUSDEnsure2Digits(
                            userSelectedPaymentTokenUsdcBalance,
                          )
                        : userSelectedTokenBalance}
                      )
                    </PaymentTokenInfoText>
                    <ChevronRightIcon width={12} height={12} />
                  </>
                ) : (
                  <>
                    <PaymentTokenInfoText>
                      Select payment method
                    </PaymentTokenInfoText>
                    <ChevronRightIcon width={12} height={12} />
                  </>
                )}
              </PaymentSelectorContainer>
            </div>
            <div style={{ display: 'flex', gap: 20, width: '100%' }}>
              {selectedPaymentMethodType === 'applePay' ? (
                <ApplePayButton
                  fullWidth
                  onClick={handleApplePayPress}
                  loading={
                    applePayStarting ||
                    applePayProcessing ||
                    waitingForApplePayBalance ||
                    isLoadingGiftFees
                  }
                  disabled={
                    waitingForApplePayBalance ||
                    feeCalculations.totalAmount < 5 ||
                    isLoadingGiftFees
                  }
                />
              ) : (
                <Button
                  fullWidth
                  clickAnimation='bouncy'
                  loading={loadingTokenAmount || isLoadingGiftFees}
                  onClick={createGiftHandler}
                  disabled={!selectedPaymentMethodType}
                >
                  Purchase Gift
                </Button>
              )}
            </div>
            {feedback && <Message>{feedback}</Message>}
          </FormContainer>
        </ContentRow>
      </DemoContainer>
    </>
  );
};

export default Confirmation;
